import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';
import { BehaviorSubject, Observable, throwError, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpHelper } from '../helpers/http-helper';
import { IBusinessOperationQualifier, IEntity, ILLCompany, ILLFormDetail, ILLGenericKeys, ILLRevisionHistory, IServerSettings } from '../models/index';
import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

@Injectable()
export class LLFormService extends Subject<DataStateChangeEventArgs> {

    private action: string;
    private data: any;
    private settings: IServerSettings;
    private apiUrl: string;
    apiResult: any;
    httpHelper: HttpHelper;
    allQualifiers: IBusinessOperationQualifier[];
    bOList: IBusinessOperationQualifier[];
    INGREDIENTS_SEARCH_MAX_COUNT = 1000;

    llFormProductLabelerCodeSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormHeaderSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormProductDetailsSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormProductInformationSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormActiveIngridientsSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormInActiveIngridientsSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormProductCharacteristicsSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormPackagingSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormMarketingSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormLabelerSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormRegistrantSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormEstablishmentSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    llFormKitPartSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';

    constructor (private http: HttpClient, private settingsService: SettingsService, private authService: AuthenticationService, private storageManagerService: StorageManagerService) {
        super();

        // Define an instance of the http helper class that will be used to create httpHeader for api calls
        this.httpHelper = new HttpHelper(http, authService, storageManagerService);
    }

    // Get a list of LL forms for the dashboard
    getLLForms(): Observable<any[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LLForms/LLDashboard`;

        return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get LL form data by revisionid
    getLLFormById(revisionId: string): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LLForms/${ revisionId }`;

        return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get LL form data by revisionid
    getLLHighLevlInfoById(revisionId: string): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LLForms/ExpiredLL/${ revisionId }`;

        return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get product detail info
    getLLFormProductDetail(revisionId: string): Observable<ILLFormDetail[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LLForms/GetLLRevisionProduct/${ revisionId }`;

        return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get product detail info
    searchIngredients(srchText: string): Observable<ILLGenericKeys[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LLForms/IngredientSearch?topN=${ this.INGREDIENTS_SEARCH_MAX_COUNT }&searchTerm=${ srchText }`;

        return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Create a draft version of a new LL form
    createLLForm(formValues) {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/LLForms/InsertLLForm';

        return this.http.post(`${ this.apiUrl }/${ this.action }`, formValues, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get a list of product types
    getProductTypes(): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/CommonData/Vocabularies?vocabType=doc_Type';

        return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get a list of Cosmetic Products and categories
    getCosmeticProducts(): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/CommonData/CosmeticCategoryAndProduct';

        return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get ssdp vacabularies
    getSSDPVocabularies(): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/CommonData/Vocabularies';

        return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get CompanyTypes By CustomerGuid
    getLCompanyTypesByCustomerGuid(customerGuid: any): Observable<ILLCompany[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/CommonData/CompanyTypesByCustomerGuid`;

        const serviceUrl = `${ this.apiUrl }/${ this.action }`;

        return this.http.get(serviceUrl, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get revision history
    getRevisionHistory(revisionId: number): Observable<ILLRevisionHistory[]> {

        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LLForms/LLVersionHistory?llRevisionId=${ revisionId }`;

        return this.http.get(`${ this.apiUrl }/${ this.action }`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Save LL Form as a draft
    saveLLForm(llData, modifiedByUser: string, draftMode: boolean) {
        let headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LLForms?isDraftMode=${ draftMode }`;

        headers = headers.set('modifiedByUser', modifiedByUser);

        // Catch the $type getting stripped out of Parts
        if(llData.products && llData.products.length > 0){
            for(let i = 0; i < llData.products.length; i++){
                if(llData.products[i].$type === 'RTCD_LLKit'){
                    for(let x = 0; x < llData.products[i].parts.length; x++){
                        llData.products[i].parts[x].$type = 'RTCD_LLPart';
                    }
                }
            }
        }
        

        return this.http.post(`${ this.apiUrl }/${ this.action }`, llData, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Check if user previously saved item are a valid entry in the LOV, if not, try to match by text,
    // if still can not find a match, display a warning messaage to the user
    cloneKitProduct(llData, modifiedByUser: string, itemIndex: number) {
        let headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/LLForms/CloneItem?itemIndex=${itemIndex}&modifiedByUser=${modifiedByUser}`;

        headers = headers.set('modifiedByUser', modifiedByUser);

        return this.http.post(`${ this.apiUrl }/${ this.action }`, llData, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Check if user previously saved item are a valid entry in the LOV, if not, try to match by text,
    // if still can not find a match, display a warning messaage to the user
    checkForLOVSelection(drpLOV: any[], userSelectId: string, userSelectDesc: string): any {
        let newId = userSelectId;
        let bIsEntryValid = false;
        let invalidEntry = '';

        if (userSelectId && drpLOV) {
            let match = drpLOV.filter(x => x.id === userSelectId);
            // Id does not match, let's check if the text matches
            if (match.length === 0) {
                match = drpLOV.filter(x => x.labelAndCode === userSelectDesc);
                if (match.length > 0) {
                    newId = match[0].id;
                } else {
                    bIsEntryValid = true;
                    invalidEntry = userSelectDesc;
                    newId = '0';
                }
            }
        }
        return [newId, bIsEntryValid, invalidEntry];
  }

  /**
 * Deep clones the given AbstractControl, preserving values, validators, async validators, and disabled status.
 * @param control AbstractControl
 * @returns AbstractControl
 */
cloneAbstractControl<T extends AbstractControl>(control: T): T {
  let newControl: T;

  if (control instanceof UntypedFormGroup) {
    const formGroup = new UntypedFormGroup({}, control.validator, control.asyncValidator);
    const controls = control.controls;

    Object.keys(controls).forEach(key => {
      formGroup.addControl(key, this.cloneAbstractControl(controls[key]));
    });

    newControl = formGroup as any;
  } else if (control instanceof UntypedFormArray) {
    const formArray = new UntypedFormArray([], control.validator, control.asyncValidator);

    control.controls.forEach(formControl => formArray.push(this.cloneAbstractControl(formControl)))

    newControl = formArray as any;
  } else if (control instanceof UntypedFormControl) {
    newControl = new UntypedFormControl(control.value, control.validator, control.asyncValidator) as any;
  } else {
    throw new Error('Error: unexpected control value');
  }

  if (control.disabled) {
    newControl.disable({ emitEvent: false });
  }

  return newControl;
}


   // deep copy of an object. Removes references to the original object.
    deepCopy<T>(value: T): any {

        if (typeof value !== 'object' || value === null) {
            return value;
        }
        if (Array.isArray(value)) {
            return this.deepArray(value);
        }
        return this.deepObject(value);
    }

    deepObject<T>(source: T) {

        const result = {};

        Object.keys(source).forEach((key) => {

            const value = source[key];
            result[key] = this.deepCopy(value);
        }, {});
        return result as T;
    }

    deepArray<T extends any[]>(collection: T) {
        return collection.map((value) => {
            return this.deepCopy(value);
        });
    }


    private extractData(response: Response) {
        if (response.status < 200 || response.status >= 300) {
            throw new Error('Bad response status: ' + response.status);
        }

        const body = response;
        return body || {};
    }

    // Error handling
    private handleErrors(error: any): Observable<any> {
      const GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';
      const bDisplayDetailMsg = error && error.error && error.error.isBusinessErrorMessage ? true : isDevMode() ? true : false;
      error.error = error && error.error && error.error.message && bDisplayDetailMsg ? error.error.message : GENERIC_ERROR_MSG;

      return throwError(error.error);
    }
}
